import EventEmitter from '../Utils/EventEmitter';
import { Config } from '../Config';

/**
 * Sizes class
 * Handles viewport dimensions and resize events
 */
export class Sizes extends EventEmitter {
  // Static instance property
  static instance = null;

  /**
   * Get the singleton instance of the class
   * @returns {Sizes} The singleton instance
   */
  static getInstance() {
    if (!Sizes.instance) {
      Sizes.instance = new Sizes();
    }
    return Sizes.instance;
  }

  /**
   * Constructor
   */
  constructor() {
    // Ensure this is a singleton
    if (Sizes.instance) {
      return Sizes.instance;
    }

    super();

    // Check if main has is-coming-soon class
    this.main = document.getElementById('main');
    this.isComingSoon = this.main && this.main.classList.contains('is-coming-soon');

    // Initial dimensions
    this.updateDimensions();

    // Resize handler with debounce
    this.resizeTimeout = null;
    this.isResizing = false;
    
    this.bindAll();

    window.addEventListener('resize', this.onResize);

    // Orientation change handler
    window.addEventListener('orientationchange', this.onOrientationChange);

    // Set instance
    Sizes.instance = this;
  }
  
  bindAll(){
    this.onResize = this.onResize.bind(this);
    this.onOrientationChange = this.onOrientationChange.bind(this);
  }

  isMobileWithAutoHideToolbar() {
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    // Additional checks for iOS Safari
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    
    // Additional checks for Android Chrome
    const isAndroid = /Android/.test(navigator.userAgent);
    
    return isMobile && (isIOS || isAndroid);
  }

  getIsTouchDevice() {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }

  /**
   * Update dimension values
   */
  updateDimensions() {
    this.isTouchDevice = this.getIsTouchDevice();

    // Width and height
    this.width = window.innerWidth;
    //this.height = window.innerHeight;
    this.height = this.isTouchDevice ? window.screen.height : window.innerHeight;
    this.canvasPadding = this.isTouchDevice ? 0 : 0.25;
    this.canvasHeight = this.height * (1 + this.canvasPadding * 2);

    // Pixel ratio (limit to 2 for performance)
    this.pixelRatio = Math.min(window.devicePixelRatio, 2);

    // Aspect ratio
    this.aspectRatio = this.width / this.height;

    // Device type
    this.isMobile = this.width < 768;
    this.isTablet = this.width >= 768 && this.width < 1024;
    this.isDesktop = this.width >= 1024;

    // Calculate frustum size
    this.frustumSize = this.calculateFrustumSize();
    this.worldUnit = this.frustumSize.height / this.height;
    this.documentHeight = document.body.scrollHeight;
    this.scrollableHeight = this.documentHeight - this.height;
    this.worldDocumentHeight = this.documentHeight * this.worldUnit;
    this.worldScrollableHeight = this.worldDocumentHeight - this.frustumSize.height;
  }

  /**
   * Calculate the size of the frustum in world units
   * @returns {Object} - Frustum size in world units
   */
  calculateFrustumSize() {
    const cameraDistance = Config.camera.initialPosition.z;
    const fovDegrees = Config.camera.fov;
    const fovRadians = fovDegrees * (Math.PI / 180);

    const height = 2 * Math.tan(fovRadians / 2) * cameraDistance;
    const width = height * this.aspectRatio;

    return {
      height,
      width
    };
  }

  /**
   * Handle resize event with debounce
   */
  onResize() {
    // Set resizing flag immediately
    this.isResizing = true;

    // Clear previous timeout
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }

    // Set timeout for debounce
    this.resizeTimeout = setTimeout(() => {     
      this.resizeTimeout = null;

      const deltaY = window.innerHeight - this.height;
      if((this.isMobile || this.isTablet) && deltaY <= 100 && window.innerWidth === this.width){
        this.isResizing = false;
        return;
      }

      this.updateDimensions();

      // Trigger resize event
      this.trigger('resize', [{
        width: this.width,
        height: this.height,
        pixelRatio: this.pixelRatio,
        aspectRatio: this.aspectRatio,
        worldUnit: this.worldUnit,
        frustumSize: this.frustumSize
      }]);

      // Reset resizing flag after a short delay to ensure all resize handlers have completed
      setTimeout(() => {
        this.isResizing = false;
      }, 200);
    }, 200); // 200ms debounce
  }

  /**
   * Convert HTML pixels to Three.js world units
   * @param {Number} pixels - Number of HTML pixels
   * @returns {Number} - Equivalent size in Three.js world units
   */
  pixelsToWorldUnits(pixels) {
    return pixels * this.worldUnit;
  }

  /**
   * Convert Three.js world units to HTML pixels
   * @param {Number} worldUnits - Size in Three.js world units
   * @returns {Number} - Equivalent size in HTML pixels
   */
  worldUnitsToPixels(worldUnits) {
    return worldUnits / this.worldUnit;
  }

  /**
   * Handle orientation change
   */
  onOrientationChange() {
    // Set resizing flag immediately
    this.isResizing = true;

    // Orientation changes can take time to complete
    // Wait a bit before updating dimensions
    setTimeout(() => {
      this.updateDimensions();

      // Trigger orientation change event
      this.trigger('orientationChange', [{
        width: this.width,
        height: this.height,
        aspectRatio: this.aspectRatio,
        worldUnit: this.worldUnit,
        frustumSize: this.frustumSize
      }]);

      // Reset resizing flag after a short delay
      setTimeout(() => {
        this.isResizing = false;
      }, 100);
    }, 200);
  }

  /**
   * Get current viewport dimensions
   * @returns {Object} - Viewport dimensions
   */
  getViewport() {
    return {
      width: this.width,
      height: this.height,
      pixelRatio: this.pixelRatio,
      aspectRatio: this.aspectRatio,
      worldUnit: this.worldUnit,
      frustumSize: this.frustumSize
    };
  }

  /**
   * Check if viewport matches a specific device type
   * @param {String} type - Device type (mobile, tablet, desktop)
   * @returns {Boolean} - True if viewport matches type
   */
  isDeviceType(type) {
    switch(type.toLowerCase()) {
      case 'mobile': return this.isMobile;
      case 'tablet': return this.isTablet;
      case 'desktop': return this.isDesktop;
      default: return false;
    }
  }
}
